/**
 * API Configuration and URL Building
 *
 * Handles API base URL resolution and endpoint URL construction
 * @module portal/modules/api/config
 */

import { DEFAULT_API_BASE, RELATIVE_FUNCTION_BASE, type Config } from '../config.js';

export type ApiConfig = Config | { apiBaseUrl?: string } | null;

type WindowWithConfig = Window & { __LOTLINGO_CONFIG__?: Config };

let apiBase: string | null = null;

/**
 * Resolve global configuration from window
 */
export function resolveGlobalConfig(): ApiConfig {
  if (typeof window === 'undefined') return null;
  const win = window as WindowWithConfig;
  return win.__LOTLINGO_CONFIG__ ?? null;
}

/**
 * Determine the API base URL from configuration
 */
export function determineApiBase(cfg?: ApiConfig): string {
  if (apiBase) return apiBase;

  let base = (cfg?.apiBaseUrl || '').toString().trim();
  const hadCfgBase = base.length > 0;

  if (!hadCfgBase) {
    base = DEFAULT_API_BASE;
  }

  try {
    const resolved = new URL(base, window.location.origin);
    const host = (window.location.hostname || '').toLowerCase();
    const isFunctionHost = /\.azurewebsites\.net$/i.test(resolved.hostname || '');
    const prefersRelative =
      /\.azurestaticapps\.net$/i.test(host) ||
      host === 'lotlingo.com' ||
      host.endsWith('.lotlingo.com');

    if (!hadCfgBase && isFunctionHost && prefersRelative) {
      base = RELATIVE_FUNCTION_BASE;
    }
  } catch (error) {
    // Ignore URL parsing errors
  }

  if (!/^https?:/i.test(base) && base.charAt(0) !== '/') {
    base = '/' + base;
  }

  const normalized: string = base.replace(/\/\/+$/, '') || RELATIVE_FUNCTION_BASE;
  apiBase = normalized;
  return normalized;
}

/**
 * Build API URL from path
 */
export function apiUrl(cfgOrPath?: ApiConfig | string, maybePath?: string): string {
  let cfg: ApiConfig | null = null;
  let path: string | undefined;

  if (typeof cfgOrPath === 'string' && maybePath == null) {
    path = cfgOrPath;
    cfg = resolveGlobalConfig();
  } else if (cfgOrPath && typeof cfgOrPath === 'object') {
    cfg = cfgOrPath as ApiConfig;
    path = maybePath;
  } else {
    cfg = resolveGlobalConfig();
    path = maybePath;
  }

  const base = determineApiBase(cfg);
  const clean = (path || '').toString().replace(/^\/+/, '');
  if (!clean) return base;
  if (base === '/') return '/' + clean;
  return base + '/' + clean;
}
